import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ClipboardList, Plus, X, Pencil, Check, Users, User, AlertTriangle, PackageOpen, ChevronDown, ChevronRight } from "lucide-react";
import OwnerBadge from "./khreis/OwnerBadge";
import RoutinePanel from "./khreis/RoutinePanel";
import PickupTaskDialog from "./khreis/PickupTaskDialog";
import { fmtDateTime } from "./khreis/utils";

export default function KhreisTask() {
  const { isAdmin } = useAuth();
  const [scope, setScope] = useState("personal"); // "personal" | "all" (admin only)
  return (
    <div className="p-3 sm:p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardList size={22} className="text-amber-700"/>
          <h1 className="text-2xl font-bold">KHREIS TASK</h1>
          <span className="text-xs text-zinc-500">Task curent + Task routine</span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5 bg-zinc-100 rounded-md p-1" data-testid="scope-toggle">
            <button
              type="button"
              onClick={() => setScope("personal")}
              className={`text-xs font-bold px-3 py-1 rounded transition-colors flex items-center gap-1 ${scope === "personal" ? "bg-white shadow text-amber-700" : "text-zinc-600 hover:text-zinc-900"}`}
              data-testid="scope-personal"
            >
              <User size={12}/> Personal
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              className={`text-xs font-bold px-3 py-1 rounded transition-colors flex items-center gap-1 ${scope === "all" ? "bg-white shadow text-amber-700" : "text-zinc-600 hover:text-zinc-900"}`}
              data-testid="scope-all"
            >
              <Users size={12}/> Toți utilizatorii
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CurrentPanel scope={scope} isAdmin={isAdmin}/>
        <RoutinePanel scope={scope}/>
      </div>
    </div>
  );
}

function CurrentPanel({ scope, isAdmin }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [showDone, setShowDone] = useState(true);
  // Persoane + locații (pentru dialog-ul de preluare PSA — fetch on-demand)
  const [persons, setPersons] = useState([]);
  const [locations, setLocations] = useState([]);
  // Dialog state pentru execuția taskului de pickup
  const [pickupTask, setPickupTask] = useState(null);
  const [pickupRows, setPickupRows] = useState({});
  const [pickupBulkDest, setPickupBulkDest] = useState("");
  const [pickupBusy, setPickupBusy] = useState(false);
  const readOnly = scope === "all"; // în "Toți utilizatorii" admin doar vizualizează — nu editează al altora

  const load = async () => {
    try {
      const { data } = await api.get(`/khreis-task/current${scope === "all" ? "?scope=all" : ""}`);
      setItems(data.rows || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  // Pre-fetch persoane + locații o singură dată — folosite în dialog-ul de preluare PSA
  useEffect(() => {
    api.get("/persons").then((r) => setPersons((r.data || []).filter((p) => p.active !== false))).catch(() => setPersons([]));
    api.get("/inventory/meta").then((r) => setLocations(r.data?.locations || [])).catch(() => setLocations([]));
  }, []);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post("/khreis-task/current", { text: t });
      setText("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (it) => {
    // Pentru task-uri PSA pickup obligatorii — la bifare se deschide dialog, nu toggle direct
    if (!it.done && it.kind === "psa_pickup_dismissed") {
      openPickupDialog(it);
      return;
    }
    try {
      await api.put(`/khreis-task/current/${it.id}/done`, { done: !it.done });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (it) => {
    if (it.kind === "psa_pickup_dismissed") {
      toast.error("Task-urile de preluare PSA obligatorii nu pot fi șterse — execută-le ca rezolvate");
      return;
    }
    if (!window.confirm("Ștergi acest task?")) return;
    try {
      await api.delete(`/khreis-task/current/${it.id}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveEdit = async () => {
    const t = editText.trim();
    if (!t) return;
    try {
      await api.put(`/khreis-task/current/${editingId}`, { text: t });
      setEditingId(null);
      setEditText("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // === Dialog preluare PSA ===
  const openPickupDialog = (task) => {
    const items = (task.meta && task.meta.items) || [];
    const init = {};
    for (const it of items) init[it.id] = { selected: true, dest: "" };
    setPickupRows(init);
    setPickupBulkDest("");
    setPickupTask(task);
  };
  const closePickup = () => {
    if (pickupBusy) return;
    setPickupTask(null);
    setPickupRows({});
    setPickupBulkDest("");
  };
  const updateRow = (iid, patch) => setPickupRows((p) => ({ ...p, [iid]: { ...(p[iid] || {}), ...patch } }));
  const applyBulkDest = () => {
    if (!pickupBulkDest) { toast.error("Alege destinația comună"); return; }
    setPickupRows((prev) => {
      const next = { ...prev };
      for (const iid of Object.keys(next)) if (next[iid].selected) next[iid] = { ...next[iid], dest: pickupBulkDest };
      return next;
    });
    toast.success("Destinația aplicată pe selecție");
  };
  const submitPickup = async () => {
    const taskItems = (pickupTask?.meta?.items) || [];
    const rows = taskItems
      .filter((it) => pickupRows[it.id]?.selected)
      .map((it) => ({ item_id: it.id, dest: pickupRows[it.id].dest || "" }));
    if (rows.length === 0) { toast.error("Selectează cel puțin un item"); return; }
    const missing = rows.filter((r) => !r.dest);
    if (missing.length > 0) { toast.error(`${missing.length} item${missing.length === 1 ? "" : "e"} fără destinație`); return; }
    setPickupBusy(true);
    try {
      const r = await api.post(`/khreis-task/current/${pickupTask.id}/execute-pickup`, { items: rows });
      const ok = r.data?.ok || 0;
      const err = r.data?.err || 0;
      if (ok > 0) toast.success(`✓ ${ok} PSA preluat${ok === 1 ? "" : "e"}${err > 0 ? ` · ${err} cu erori` : ""}`);
      else toast.error("Nicio operațiune nu a reușit");
      closePickup();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPickupBusy(false); }
  };

  // Separare pending vs done — ambele sortate descrescător după created_at (cele mai noi sus)
  const pendingSorted = useMemo(
    () => items.filter((i) => !i.done).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [items],
  );
  const doneSorted = useMemo(
    () => items.filter((i) => i.done).sort((a, b) => (b.done_at || b.created_at || "").localeCompare(a.done_at || a.created_at || "")),
    [items],
  );
  const pendingCount = pendingSorted.length;
  const doneCount = doneSorted.length;
  const pickupPendingCount = pendingSorted.filter((i) => i.kind === "psa_pickup_dismissed").length;

  // Helper: render un singur item (refolosit între pending & done)
  const renderItem = (it) => {
    const isPickup = it.kind === "psa_pickup_dismissed";
    const meta = it.meta || {};
    const metaItems = meta.items || [];
    const cls = it.done
      ? "bg-emerald-50 border-emerald-300"
      : isPickup
        ? "bg-red-50 border-2 border-red-500 ring-2 ring-red-200 animate-pulse-red-soft shadow-md"
        : "bg-white border-zinc-200 hover:border-amber-300";
    return (
      <div
        key={it.id}
        className={`flex items-start gap-2 px-3 py-2 rounded border group transition-colors ${cls}`}
        data-testid={`current-${it.id}`}
      >
        <input
          type="checkbox"
          checked={!!it.done}
          onChange={() => { if (!readOnly) toggle(it); }}
          disabled={readOnly}
          className={`w-4 h-4 mt-1 cursor-pointer flex-shrink-0 disabled:cursor-not-allowed ${isPickup && !it.done ? "accent-red-600" : "accent-emerald-600"}`}
          data-testid={`current-check-${it.id}`}
          title={isPickup && !it.done ? "Bifează pentru a executa preluarea" : ""}
        />
        <div className="flex-1 min-w-0">
          {editingId === it.id ? (
            <div className="flex items-center gap-1">
              <Input
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } else if (e.key === "Escape") setEditingId(null); }}
                className="h-8 text-sm"
                autoFocus
              />
              <Button onClick={saveEdit} size="sm" className="bg-amber-600 hover:bg-amber-700 h-8"><Check size={12}/></Button>
              <Button onClick={() => setEditingId(null)} variant="outline" size="sm" className="h-8"><X size={12}/></Button>
            </div>
          ) : (
            <>
              <div className={`text-sm flex items-center gap-1.5 flex-wrap ${it.done ? "line-through text-emerald-700" : isPickup ? "text-red-900 font-bold" : "text-zinc-800"}`}>
                {isPickup && !it.done && (
                  <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-extrabold uppercase tracking-wider flex items-center gap-1">
                    <AlertTriangle size={10}/> OBLIGATORIU
                  </span>
                )}
                <span className="flex-1">{it.text}</span>
                {readOnly && <OwnerBadge item={it}/>}
              </div>
              {/* Listă vizuală items pentru task-uri pickup */}
              {isPickup && metaItems.length > 0 && !it.done && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`current-pickup-items-${it.id}`}>
                  {metaItems.map((mi) => (
                    <span
                      key={mi.id}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-red-300 text-red-900 font-mono"
                      title={`${mi.name}${mi.psa_size ? " · " + mi.psa_size : ""}${mi.serial ? " · SN " + mi.serial : ""}${mi.location ? " · @" + mi.location : ""}`}
                    >
                      🦺 {mi.name}{mi.psa_size ? ` · ${mi.psa_size}` : ""}{mi.serial ? ` · ${mi.serial}` : ""}
                    </span>
                  ))}
                </div>
              )}
              {isPickup && it.done && (meta.executed_ok || meta.executed_err) ? (
                <div className="text-[10px] text-emerald-700 mt-0.5">
                  ✓ {meta.executed_ok || 0} item{(meta.executed_ok || 0) === 1 ? "" : "e"} preluat{(meta.executed_ok || 0) === 1 ? "" : "e"}
                  {meta.executed_err ? ` · ${meta.executed_err} cu erori` : ""}
                </div>
              ) : null}
              <div className="text-[10px] text-zinc-400 mt-0.5">
                Adăugat {fmtDateTime(it.created_at)}
                {it.done && it.done_at && (
                  <span className="text-emerald-700"> · ✓ Rezolvat {fmtDateTime(it.done_at)}</span>
                )}
              </div>
            </>
          )}
        </div>
        {editingId !== it.id && !readOnly && !it.done && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {isPickup ? (
              <button
                type="button"
                onClick={() => openPickupDialog(it)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Execută preluarea PSA"
                data-testid={`current-exec-${it.id}`}
              >
                <PackageOpen size={14}/>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setEditingId(it.id); setEditText(it.text); }}
                  className="text-zinc-500 hover:text-zinc-900 p-1"
                  title="Editează"
                  data-testid={`current-edit-${it.id}`}
                >
                  <Pencil size={12}/>
                </button>
                <button
                  type="button"
                  onClick={() => remove(it)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Șterge"
                  data-testid={`current-delete-${it.id}`}
                >
                  <X size={14}/>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card-flat p-4 sm:p-5" data-testid="panel-current">
      <div className="flex items-center justify-between pb-3 border-b mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-amber-700"/>
          <h2 className="font-display text-lg font-bold">TASK CURENT</h2>
          {readOnly && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">👁 Vizualizare globală</span>}
          {pickupPendingCount > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-300 animate-pulse" title="Task-uri obligatorii de preluare PSA în aștepare">
              ⚠ {pickupPendingCount} PSA pickup
            </span>
          )}
        </div>
        <div className="text-[10px] text-zinc-500">
          <strong className="text-amber-700">{pendingCount}</strong> de făcut · <strong className="text-emerald-700">{doneCount}</strong> rezolvate
        </div>
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2 mb-3">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Adaugă un task… (Enter)"
            data-testid="current-new-input"
          />
          <Button onClick={add} disabled={!text.trim() || busy} className="bg-amber-600 hover:bg-amber-700" data-testid="current-add-btn">
            <Plus size={14}/>
          </Button>
        </div>
      )}

      {/* PENDING */}
      {pendingSorted.length === 0 ? (
        <div className="text-xs text-zinc-400 italic py-6 text-center border border-dashed rounded">
          Niciun task curent. Adaugă primul!
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1" data-testid="current-pending-list">
          {pendingSorted.map(renderItem)}
        </div>
      )}

      {/* DONE — secțiune separată */}
      {doneSorted.length > 0 && (
        <div className="mt-4 pt-3 border-t-2 border-dashed border-emerald-200">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-2 text-xs font-bold text-emerald-800 hover:text-emerald-900 mb-2"
            data-testid="current-toggle-done"
          >
            {showDone ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <span className="uppercase tracking-wider">✓ Efectuate</span>
            <span className="text-zinc-500 font-normal">· {doneCount}</span>
          </button>
          {showDone && (
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1 opacity-80" data-testid="current-done-list">
              {doneSorted.map(renderItem)}
            </div>
          )}
        </div>
      )}

      {/* Dialog: execuție task PSA pickup */}
      <PickupTaskDialog
        task={pickupTask}
        rows={pickupRows}
        bulkDest={pickupBulkDest}
        busy={pickupBusy}
        persons={persons}
        locations={locations}
        onClose={closePickup}
        onSetBulkDest={setPickupBulkDest}
        onApplyBulk={applyBulkDest}
        onUpdateRow={updateRow}
        onSubmit={submitPickup}
      />
    </div>
  );
}

